import React, { useRef } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";
import Counter from "./Counter";
import MagneticButton from "./MagneticButton";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/39e5a5b9-5e3d-485e-a045-ea871eda4997/images/18ee02ea2ceb46579feb203d75c7b32dc00f8ae7a6f9955eefc13711d47c822b.png";

const item = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.7, ease: [0.2, 0.7, 0.2, 1] },
  }),
};

const METRICS = [
  { value: 14, prefix: "", suffix: "+", label: "Years in B2B tech marketing" },
  { value: 1, prefix: "$", suffix: "M+", label: "Annual budgets managed" },
  { value: 9, prefix: "", suffix: "×", label: "Platform certifications" },
  { value: 40, prefix: "", suffix: "+", label: "Brands accelerated" },
];

export default function Hero() {
  const heroRef = useRef(null);

  return (
    <section
      id="top"
      ref={heroRef}
      data-testid="hero-section"
      className="relative pt-[120px] md:pt-[160px] pb-16 md:pb-24"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Status row */}
        <motion.div
          initial="hidden"
          animate="show"
          custom={0}
          variants={item}
          data-testid="hero-status-strip"
          className="flex items-center justify-between border-t border-b border-line py-3 mb-8 md:mb-12 font-mono text-[11px] uppercase tracking-[0.22em] text-ink2 gap-4"
        >
          <span className="flex items-center gap-3">
            <span className="w-2 h-2 bg-signal animate-pulse" />
            <span className="hidden sm:inline">Marketing as a craft — not a cost center</span>
            <span className="sm:hidden">A craft, not a cost center</span>
          </span>
          <span className="hidden md:inline text-center">
            Quietly compounding B2B pipeline since 2012
          </span>
          <span className="whitespace-nowrap">
            Remote <span className="text-signal">→</span> Worldwide
          </span>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start">
          {/* Left: Text */}
          <div className="md:col-span-8">
            <motion.p
              initial="hidden"
              animate="show"
              custom={1}
              variants={item}
              data-testid="hero-overline"
              className="font-mono text-[11px] md:text-xs uppercase tracking-[0.22em] text-signal mb-6"
            >
              ◆ Senior B2B Digital Marketing Consultant
            </motion.p>

            <motion.h1
              initial="hidden"
              animate="show"
              custom={2}
              variants={item}
              data-testid="hero-heading"
              className="font-heading font-black text-[56px] sm:text-[88px] md:text-[120px] lg:text-[148px] leading-[0.88] tracking-tightest text-ink"
            >
              Abhinav<br />
              <span className="inline-flex items-center gap-3">
                Dhiman
                <motion.span
                  initial={{ rotate: -25, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  transition={{ delay: 0.6, duration: 0.9, ease: [0.2, 0.7, 0.2, 1] }}
                  className="inline-flex"
                >
                  <ArrowDownRight
                    className="-mb-3 md:-mb-6 w-10 h-10 md:w-20 md:h-20 text-signal"
                    strokeWidth={1.5}
                  />
                </motion.span>
              </span>
            </motion.h1>

            <motion.p
              initial="hidden"
              animate="show"
              custom={3}
              variants={item}
              data-testid="hero-tagline"
              className="mt-10 font-heading text-2xl md:text-4xl font-medium leading-[1.1] tracking-tight max-w-3xl"
            >
              Empowering your digital presence with{" "}
              <span className="italic font-normal text-signal">demand, growth</span> &amp;{" "}
              <span className="italic font-normal">performance</span> marketing that moves the revenue needle.
            </motion.p>

            <motion.div
              initial="hidden"
              animate="show"
              custom={4}
              variants={item}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <MagneticButton strength={0.25}>
                <a
                  href="#contact"
                  data-testid="hero-primary-cta"
                  className="inline-flex items-center gap-3 bg-ink text-paper hover:bg-signal px-8 py-5 font-heading font-bold text-[13px] uppercase tracking-[0.2em] transition-colors"
                >
                  Start a Project <ArrowUpRight size={16} />
                </a>
              </MagneticButton>
              <MagneticButton strength={0.25}>
                <a
                  href="#blog"
                  data-testid="hero-secondary-cta"
                  className="inline-flex items-center gap-3 border border-ink px-8 py-5 font-heading font-bold text-[13px] uppercase tracking-[0.2em] hover:bg-ink hover:text-paper transition-colors"
                >
                  Explore Writing
                </a>
              </MagneticButton>
            </motion.div>
          </div>

          {/* Right: Image block — with subtle parallax on scroll */}
          <motion.div
            initial="hidden"
            animate="show"
            custom={5}
            variants={item}
            className="md:col-span-4 relative"
          >
            <motion.div
              whileHover={{ y: -6 }}
              transition={{ type: "spring", stiffness: 200, damping: 18 }}
              className="relative w-full aspect-[4/5] bg-surface border border-line overflow-hidden"
            >
              <motion.img
                src={HERO_IMG}
                alt="Digital growth strategy visualization"
                className="w-full h-full object-cover mix-blend-multiply"
                loading="eager"
                initial={{ scale: 1.08 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.6, ease: [0.2, 0.7, 0.2, 1] }}
                whileHover={{ scale: 1.04 }}
              />
              <div className="absolute left-0 right-0 bottom-0 p-4 bg-ink text-paper flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em]">File · 001</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.22em]">Portrait / Strategy</span>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Metrics strip — animated counters */}
        <motion.div
          initial="hidden"
          animate="show"
          custom={6}
          variants={item}
          data-testid="hero-metrics"
          className="mt-12 md:mt-16 grid grid-cols-2 md:grid-cols-4 border-t border-b border-ink"
        >
          {METRICS.map((m, i) => (
            <div
              key={i}
              className={`py-8 md:py-10 px-6 ${
                i < 3 ? "md:border-r border-ink" : ""
              } ${i === 2 ? "border-r border-ink md:border-r" : ""} ${
                i === 1 ? "border-l border-ink md:border-l-0" : ""
              }`}
              data-testid={`hero-metric-${i}`}
            >
              <div className="font-heading font-black text-4xl md:text-6xl leading-none tracking-tightest">
                <Counter value={m.value} prefix={m.prefix} suffix={m.suffix} />
              </div>
              <div className="mt-3 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.22em] text-ink2">
                {m.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
